// Servicio de inventario sobre Supabase (multi-dispositivo).
// API async — los componentes lo consumen con await o vía useInventario hook.
//
// Inventario maestro (0051): el descuento por venta vive en el SERVIDOR
// (trigger sobre sales + RPC mezo_descontar_inventario_venta). Este módulo:
//   - sondea la RPC tras cada venta (descontarEnServidor) y, si el servidor ya
//     descontó, bloquea el descuento del cliente (descontarPorVenta) para que
//     nunca haya doble descuento — y si la RPC no existe aún, descuenta como
//     siempre (el frontend funciona ANTES de correr la migración).
//   - carga masiva del maestro (cargarMaestro), kardex de todo el negocio
//     (kardex) y cobertura de recetas (coberturaRecetas).
// En modo mock / demo (/demo) delega en el inventario en memoria de mockApi.

import { restGet, restWrite, one } from './supabaseRest';
import { getBusinessId, supabase, USE_MOCK } from './supabaseClient';
import * as mockApi from './mockApi';
import { normalizarNombre, calcularCobertura } from '../utils/inventarioMaestro';
import { decidirModoDescuento, crearRegistroDescuentos } from '../utils/descuentoInventario';

export const UNIDADES = ['g', 'kg', 'ml', 'L', 'ud', 'lb', 'oz'];
export const CATEGORIAS = ['Bebidas', 'Insumos', 'Empaque', 'Otros'];

// Inventario en memoria para el demo público / desarrollo sin Supabase.
const M = USE_MOCK ? mockApi.inventarioMock : null;

function bid() {
  const id = getBusinessId();
  if (!id) throw new Error('no_business');
  return id;
}

// ─── Mappers DB ↔ frontend ───────────────────────────────────────────────────

function fromItem(r) {
  if (!r) return null;
  return {
    id:            r.id,
    nombre:        r.nombre,
    unidad:        r.unidad ?? 'ud',
    stockActual:   Number(r.stock_actual) || 0,
    stockMaximo:   Number(r.stock_maximo) || 0,
    // Mínimo del inventario maestro (0051). null = no definido → semáforo por %.
    stockMinimo:   r.stock_minimo == null || r.stock_minimo === '' ? null : Number(r.stock_minimo),
    categoria:     r.categoria ?? 'Otros',
    precioCosto:   Number(r.precio_costo) || 0,
    // Caducidad (0063, piloto): 'YYYY-MM-DD' o null = no vence / no se sabe.
    venceEl:       r.vence_el ?? null,
    creadoEn:      r.created_at ?? new Date().toISOString(),
    actualizadoEn: r.updated_at ?? r.created_at ?? new Date().toISOString(),
  };
}

// origen (0051) manda; las filas viejas solo traen tipo ('perdida' → merma).
function origenDesde(m) {
  if (m.origen) return m.origen;
  return m.tipo === 'perdida' ? 'merma' : (m.tipo ?? 'ajuste');
}

function fromMov(m) {
  return {
    id:               m.id,
    itemId:           m.item_id,
    tipo:             m.tipo,
    origen:           origenDesde(m),
    cantidad:         Number(m.cantidad),
    stockResultante:  Number(m.stock_resultante),
    nota:             m.nota ?? '',
    fecha:            m.created_at,
    orderId:          m.order_id ?? null,
    userId:           m.user_id ?? null,
  };
}

// ¿El error es "esa columna no existe"? (migración 0051 sin correr). Entonces
// reintentamos sin los campos nuevos — así nada se rompe antes de migrar.
function _esColumnaInexistente(e) {
  return /column .* does not exist|could not find the .* column|schema cache|PGRST204/i.test(e?.message || '');
}

// Escribe con campos opcionales (nuevos en 0051) y, si la DB aún no los tiene,
// reintenta sin ellos. `prefer` se pasa tal cual a restWrite.
let _columnasNuevasOk = null;   // null = no sabemos · true/false = ya probamos
async function _escribirConFallback(method, path, base, opcionales, prefer) {
  const tieneOpcionales = opcionales && Object.keys(opcionales).length > 0;
  if (tieneOpcionales && _columnasNuevasOk !== false) {
    try {
      const r = await restWrite(method, path, { ...base, ...opcionales }, prefer);
      _columnasNuevasOk = true;
      return r;
    } catch (e) {
      if (!_esColumnaInexistente(e)) throw e;
      _columnasNuevasOk = false;
    }
  }
  return restWrite(method, path, base, prefer);
}

async function registrarMovimiento(itemId, tipo, cantidad, stockResultante, nota = '', extra = {}) {
  const base = {
    business_id:      bid(),
    item_id:          itemId,
    tipo,
    cantidad:         Number(cantidad),
    stock_resultante: Number(stockResultante),
    nota,
  };
  const opcionales = {};
  if (extra.origen)  opcionales.origen   = extra.origen;
  if (extra.orderId) opcionales.order_id = extra.orderId;
  // restWrite muestra un toast en cualquier error; el movimiento es secundario
  // (el stock ya quedó escrito), así que lo tragamos como antes.
  await _escribirConFallback('POST', '/inventory_movements', base, opcionales, 'return=minimal').catch(() => {});
}

// Semáforo del ítem. Si el maestro definió un mínimo, manda el mínimo
// (crítico al llegar a él, medio hasta 1.5×); si no, el % sobre stock_maximo.
function calcularEstado(item) {
  if (!item) return 'ok';
  const min = Number(item.stockMinimo);
  if (item.stockMinimo != null && Number.isFinite(min) && min > 0) {
    const stock = Number(item.stockActual) || 0;
    if (stock <= min)       return 'critico';
    if (stock <= min * 1.5) return 'medio';
    return 'ok';
  }
  if (!item.stockMaximo) return 'ok';
  const pct = (item.stockActual / item.stockMaximo) * 100;
  if (pct < 20) return 'critico';
  if (pct < 50) return 'medio';
  return 'ok';
}

// ─── API pública ─────────────────────────────────────────────────────────────

async function listar() {
  if (M) return M.listar();
  try {
    const rows = await restGet(
      `/inventory_items?business_id=eq.${bid()}&select=*&order=nombre.asc`
    );
    return (rows ?? []).map(fromItem);
  } catch {
    return [];
  }
}

async function obtener(id) {
  if (M) return M.obtener(id);
  try {
    return fromItem(one(await restGet(
      `/inventory_items?id=eq.${id}&business_id=eq.${bid()}&select=*&limit=1`
    )));
  } catch {
    return null;
  }
}

async function crear({ nombre, unidad, stockActual, stockMaximo, stockMinimo, categoria, precioCosto, venceEl }, { origen = 'reposicion', nota = 'Stock inicial' } = {}) {
  if (M) return M.crear({ nombre, unidad, stockActual, stockMaximo, stockMinimo, categoria, precioCosto, venceEl }, { origen, nota });
  const row = {
    business_id:  bid(),
    nombre,
    unidad:       unidad || 'ud',
    stock_actual: Number(stockActual) || 0,
    stock_maximo: Number(stockMaximo) || Number(stockActual) || 100,
    categoria:    categoria || 'Otros',
    precio_costo: Number(precioCosto) || 0,
  };
  const opcionales = {};
  if (stockMinimo != null && stockMinimo !== '') opcionales.stock_minimo = Number(stockMinimo) || 0;
  if (venceEl) opcionales.vence_el = venceEl;
  const data = fromItem(one(await _escribirConFallback('POST', '/inventory_items', row, opcionales)));
  if (data?.stockActual > 0) {
    await registrarMovimiento(data.id, 'reposicion', data.stockActual, data.stockActual, nota, { origen });
  }
  return data;
}

async function actualizar(id, cambios) {
  if (M) return M.actualizar(id, cambios);
  const update = { updated_at: new Date().toISOString() };
  if (cambios.nombre       !== undefined) update.nombre        = cambios.nombre;
  if (cambios.unidad       !== undefined) update.unidad        = cambios.unidad;
  if (cambios.stockActual  !== undefined) update.stock_actual  = Number(cambios.stockActual);
  if (cambios.stockMaximo  !== undefined) update.stock_maximo  = Number(cambios.stockMaximo);
  if (cambios.categoria    !== undefined) update.categoria     = cambios.categoria;
  if (cambios.precioCosto  !== undefined) update.precio_costo  = Number(cambios.precioCosto);
  const opcionales = {};
  if (cambios.stockMinimo  !== undefined) opcionales.stock_minimo = cambios.stockMinimo == null ? null : Number(cambios.stockMinimo);
  if (cambios.venceEl      !== undefined) opcionales.vence_el     = cambios.venceEl || null;
  return fromItem(one(await _escribirConFallback('PATCH',
    `/inventory_items?id=eq.${id}&business_id=eq.${bid()}`, update, opcionales)));
}

async function eliminar(id) {
  if (M) return M.eliminar(id);
  await restWrite('DELETE',
    `/inventory_items?id=eq.${id}&business_id=eq.${bid()}`, null, 'return=minimal');
  return { ok: true };
}

async function reponer(id, cantidad, nota = '') {
  if (M) return M.reponer(id, cantidad, nota);
  const actual = await obtener(id);
  if (!actual) return null;
  const cantidadNum = Number(cantidad) || 0;
  const nuevo = actual.stockActual + cantidadNum;
  const data = await actualizar(id, { stockActual: nuevo });
  await registrarMovimiento(id, 'reposicion', cantidadNum, nuevo, nota, { origen: 'reposicion' });
  return data;
}

async function ajustar(id, nuevoStock, nota = '') {
  if (M) return M.ajustar(id, nuevoStock, nota);
  const actual = await obtener(id);
  if (!actual) return null;
  const nuevoNum = Number(nuevoStock) || 0;
  const diferencia = nuevoNum - actual.stockActual;
  const data = await actualizar(id, { stockActual: nuevoNum });
  // Un ajuste hacia abajo con razón de pérdida/daño se clasifica como merma
  // en el kardex; el resto, ajuste.
  const esMerma = diferencia < 0 && /merma|perdid|dañad|danad|vencid|roto|derram/i.test(nota || '');
  await registrarMovimiento(id, esMerma ? 'perdida' : 'ajuste', diferencia, nuevoNum, nota,
    { origen: esMerma ? 'merma' : 'ajuste' });
  return data;
}

// ─── Descuento por venta: servidor (0051) vs cliente ─────────────────────────

// Órdenes que el servidor ya descontó en esta sesión. descontarPorVenta las
// salta → CarritoPOS / ModalDivisionCuenta pueden seguir llamándolo sin
// cambios y jamás hay doble descuento.
const _registro = crearRegistroDescuentos();
let _rpcConocida = null;        // null = no sabemos · true/false = ya sondeamos
let _rpcNoExisteDesde = 0;      // si no existe, re-sondeamos cada 5 min (por si
const REINTENTO_SONDA_MS = 5 * 60 * 1000;   // el dueño corre 0051 sin recargar)

// Pide al servidor que descuente la orden (idempotente). Devuelve
// { modo: 'servidor' | 'cliente', resultado }. Nunca lanza.
async function descontarEnServidor(orderId) {
  if (!orderId) return { modo: 'cliente', motivo: 'sin_orden' };
  // En el demo no hay servidor: el inventario en memoria se descuenta por el
  // camino del cliente (descontarPorVenta), igual que antes de 0051.
  if (M) return { modo: 'cliente', motivo: 'mock' };
  if (_rpcConocida === false && Date.now() - _rpcNoExisteDesde < REINTENTO_SONDA_MS) {
    return { modo: 'cliente', motivo: 'rpc_no_existe_cache' };
  }
  let data = null, error = null;
  try {
    ({ data, error } = await supabase.rpc('mezo_descontar_inventario_venta', { p_order_id: orderId }));
  } catch (e) {
    error = e;
  }
  const decision = decidirModoDescuento({ data, error, rpcConocida: _rpcConocida });
  if (decision.rpcExiste === true)  _rpcConocida = true;
  if (decision.rpcExiste === false) { _rpcConocida = false; _rpcNoExisteDesde = Date.now(); }
  if (decision.modo === 'servidor') _registro.marcarServidor(orderId);
  if (data?.faltantes?.length) {
    // Aviso suave: la venta salió pero algún insumo quedó en 0.
    console.warn('[inventario] insumos sin stock suficiente:', data.faltantes);
  }
  return { ...decision, resultado: data ?? null };
}

function descontadoEnServidor(orderId) { return _registro.descontadoEnServidor(orderId); }

async function descontarPorVenta(id, cantidad, ordenId) {
  // El servidor (trigger 0051) ya descontó esta orden → no repetir.
  if (!_registro.debeDescontarEnCliente(ordenId)) return null;
  if (M) return M.descontarPorVenta(id, cantidad, ordenId);
  const actual = await obtener(id);
  if (!actual) return null;
  const cantidadNum = Number(cantidad) || 0;
  const nuevo = actual.stockActual - cantidadNum;
  const data = await actualizar(id, { stockActual: nuevo });
  await registrarMovimiento(id, 'venta', -cantidadNum, nuevo, ordenId ? `Orden ${ordenId}` : '',
    { origen: 'venta', orderId: ordenId || undefined });
  return data;
}

async function movimientos(itemId) {
  if (M) return M.movimientos(itemId);
  try {
    const rows = await restGet(
      `/inventory_movements?item_id=eq.${itemId}&select=*&order=created_at.desc&limit=200`
    );
    return (rows ?? []).map(fromMov);
  } catch {
    return [];
  }
}

async function obtenerCriticos() {
  const items = await listar();
  return items.filter((i) => calcularEstado(i) === 'critico');
}

// ─── Inventario maestro (0051) ───────────────────────────────────────────────

// Carga masiva. filas = salida validada de parsearFilasMaestro:
//   [{ nombre, unidad, stock, minimo, costo, categoria }]
// modo 'reemplazar' (conteo físico: el stock queda como dice el Excel) |
//      'sumar' (compra: se suma a lo que hay).
// Crea/actualiza por nombre normalizado y registra un movimiento origen
// 'carga_maestra' por ítem con la diferencia. Devuelve
// { creados, actualizados, sinCambio, errores: [{ nombre, error }] }.
async function cargarMaestro(filas, { modo = 'reemplazar', nota = '' } = {}) {
  if (M) return M.cargarMaestro(filas, { modo, nota });
  const existentes = await listar();
  const porNombre = new Map(existentes.map((it) => [normalizarNombre(it.nombre), it]));
  const notaMov = nota || (modo === 'sumar' ? 'Carga maestra (compra)' : 'Carga maestra (conteo físico)');
  const out = { creados: 0, actualizados: 0, sinCambio: 0, errores: [] };

  // En serie: no saturar PostgREST y poder reportar exactamente qué entró.
  for (const f of filas ?? []) {
    try {
      const ex = porNombre.get(normalizarNombre(f.nombre));
      if (!ex) {
        const creado = await crear({
          nombre:      f.nombre.trim(),
          unidad:      f.unidad || 'ud',
          stockActual: f.stock,
          stockMaximo: Math.max(Number(f.stock) || 0, Number(f.minimo) * 2 || 0, 10),
          stockMinimo: f.minimo,
          categoria:   f.categoria || 'Insumos',
          precioCosto: f.costo ?? 0,
        }, { origen: 'carga_maestra', nota: notaMov });
        if (creado) porNombre.set(normalizarNombre(creado.nombre), creado);
        out.creados++;
        continue;
      }
      const antes   = Number(ex.stockActual) || 0;
      const despues = modo === 'sumar' ? antes + Number(f.stock) : Number(f.stock);
      const dif     = despues - antes;
      const cambios = {};
      if (dif !== 0)                                   cambios.stockActual = despues;
      if (f.unidad && f.unidad !== ex.unidad)         cambios.unidad      = f.unidad;
      if (f.costo != null && Number(f.costo) !== Number(ex.precioCosto || 0)) cambios.precioCosto = f.costo;
      if (f.minimo != null && Number(f.minimo) !== Number(ex.stockMinimo ?? NaN)) cambios.stockMinimo = f.minimo;
      if (f.categoria && f.categoria !== ex.categoria) cambios.categoria   = f.categoria;
      if (despues > (Number(ex.stockMaximo) || 0))    cambios.stockMaximo = despues;
      if (Object.keys(cambios).length === 0) { out.sinCambio++; continue; }
      await actualizar(ex.id, cambios);
      if (dif !== 0) {
        await registrarMovimiento(ex.id, modo === 'sumar' ? 'reposicion' : 'ajuste', dif, despues, notaMov,
          { origen: 'carga_maestra' });
      }
      out.actualizados++;
    } catch (e) {
      out.errores.push({ nombre: f?.nombre ?? '', error: e?.message || 'Error desconocido' });
    }
  }
  return out;
}

// Kardex: movimientos de TODO el negocio con nombre/unidad del ítem y quién
// lo hizo. Filtros opcionales: desde/hasta (ISO o Date), origen, itemId.
// Embed de inventory_items + mapa user_id → nombre (team_members), ambos
// best-effort: si el embed falla (caché de esquema, 0051 sin correr) cae a
// la consulta plana y resuelve los nombres con una segunda lectura.
async function kardex({ desde, hasta, origen, itemId, limite = 1000 } = {}) {
  if (M) return M.kardex({ desde, hasta, origen, itemId, limite });
  const biz = bid();
  const iso = (d) => (d instanceof Date ? d.toISOString() : d);
  let q = `business_id=eq.${biz}&order=created_at.desc&limit=${limite}`;
  if (desde)  q += `&created_at=gte.${encodeURIComponent(iso(desde))}`;
  if (hasta)  q += `&created_at=lte.${encodeURIComponent(iso(hasta))}`;
  if (itemId) q += `&item_id=eq.${itemId}`;

  let rows = null;
  try {
    rows = await restGet(`/inventory_movements?${q}&select=*,inventory_items(nombre,unidad)`);
  } catch {
    try { rows = await restGet(`/inventory_movements?${q}&select=*`); } catch { rows = []; }
  }
  rows = rows ?? [];

  // Nombres de ítems si el embed no vino.
  let itemsById = null;
  if (rows.some((r) => !r.inventory_items)) {
    const items = await listar();
    itemsById = Object.fromEntries(items.map((i) => [i.id, i]));
  }
  // Quién movió: user_id → nombre del empleado (team_members).
  let nombrePorUser = {};
  if (rows.some((r) => r.user_id)) {
    try {
      const tm = await restGet(`/team_members?business_id=eq.${biz}&select=user_id,name,email`);
      nombrePorUser = Object.fromEntries((tm ?? []).filter((t) => t.user_id)
        .map((t) => [t.user_id, t.name || (t.email ? t.email.split('@')[0] : null)]));
    } catch { /* sin nombres: columna "por" vacía */ }
  }

  const lista = rows.map((r) => {
    const base = fromMov(r);
    const it = r.inventory_items ?? itemsById?.[r.item_id] ?? null;
    return {
      ...base,
      itemNombre: it?.nombre ?? '—',
      unidad:     it?.unidad ?? '',
      por:        r.user_id ? (nombrePorUser[r.user_id] ?? null) : null,
    };
  });
  // El filtro por origen se aplica en cliente: así también cubre filas viejas
  // sin columna origen (0051 sin correr) que se clasifican por tipo.
  return origen ? lista.filter((m) => m.origen === origen) : lista;
}

// ¿Qué productos activos NO descuentan nada al venderse? Lee los productos
// directo (shape mínima) para no depender de api.js (que importa este módulo).
async function coberturaRecetas() {
  if (M) return M.coberturaRecetas();
  try {
    const rows = await restGet(
      `/products?business_id=eq.${bid()}&select=id,name,disponible,receta_inventario`
    );
    const productos = (rows ?? []).map((p) => ({
      id: p.id, nombre: p.name, disponible: p.disponible !== false,
      inventarioAsociado: p.receta_inventario ?? { activo: false, items: [] },
    }));
    return calcularCobertura(productos);
  } catch {
    return { totalProductos: 0, conReceta: 0, sinReceta: [] };
  }
}

// ─── Inventario Nivel 2 ───────────────────────────────────────────────────────

// Consumo reciente por item, leído de los movimientos de VENTA (los registra
// el servidor (0051) o descontarPorVenta al vender un producto con receta).
// Una sola consulta para todos los items. Devuelve { [itemId]: { total, porDia } }.
async function consumoReciente(dias = 14) {
  if (M) return M.consumoReciente(dias);
  const desde = new Date(Date.now() - dias * 86400000).toISOString();
  try {
    const rows = await restGet(
      `/inventory_movements?business_id=eq.${bid()}&tipo=eq.venta` +
      `&created_at=gte.${desde}&select=item_id,cantidad,created_at`
    );
    const acc = {};
    for (const r of (rows ?? [])) {
      const id = r.item_id;
      if (!acc[id]) acc[id] = { total: 0, primer: r.created_at };
      acc[id].total += Math.abs(Number(r.cantidad) || 0);
      if (r.created_at < acc[id].primer) acc[id].primer = r.created_at;
    }
    const out = {};
    const ahora = Date.now();
    for (const [id, v] of Object.entries(acc)) {
      // Promedio sobre los días reales con datos (mín 1, máx la ventana), para no
      // subestimar cuando el item lleva poco tiempo registrándose.
      const span = Math.max(1, Math.min(dias, Math.ceil((ahora - new Date(v.primer).getTime()) / 86400000)));
      out[id] = { total: v.total, porDia: v.total / span };
    }
    return out;
  } catch {
    return {};
  }
}

// Días que alcanza el stock al ritmo de consumo actual. null si no hay datos.
function diasRestantes(item, consumo) {
  const porDia = consumo?.porDia ?? 0;
  if (!porDia || porDia <= 0) return null;
  return (Number(item.stockActual) || 0) / porDia;
}

// Costo REAL de un producto a partir de su receta (ingredientes ligados al
// inventario) × el costo unitario de cada insumo. `inventarioAsociado` es
// { activo, items:[{itemId, cantidadPorVenta}] }. itemsById: { [id]: item }.
function costoReceta(inventarioAsociado, itemsById) {
  const items = inventarioAsociado?.items ?? [];
  if (!items.length) return null;
  let costo = 0;
  let faltanCostos = false;
  let ingredientesValidos = 0;
  for (const it of items) {
    const inv = itemsById[it.itemId];
    if (!inv) continue;
    ingredientesValidos++;
    if (!inv.precioCosto) faltanCostos = true;
    costo += (Number(it.cantidadPorVenta) || 0) * (Number(inv.precioCosto) || 0);
  }
  if (!ingredientesValidos) return null;
  return { costo: Math.round(costo), faltanCostos };
}

export const inventarioService = {
  listar,
  obtener,
  crear,
  actualizar,
  eliminar,
  reponer,
  ajustar,
  descontarPorVenta,
  descontarEnServidor,
  descontadoEnServidor,
  movimientos,
  obtenerCriticos,
  calcularEstado,
  consumoReciente,
  diasRestantes,
  costoReceta,
  cargarMaestro,
  kardex,
  coberturaRecetas,
  UNIDADES,
  CATEGORIAS,
};

export default inventarioService;
